import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import Typography from '@mui/material/Typography';
import { styled, useColorScheme } from '@mui/material/styles';
import AppTheme from '../../theme/AppTheme';
import ColorModeSelect from '../../theme/ColorModeSelect';
import { GoogleIcon } from './AuthIcons';
import { initiateLogin } from '../authUtils';

const BackgroundLayer = styled(Box)({
  position: 'fixed',
  inset: 0,
  backgroundImage: 'url(/loginBg.png)',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
});

const OverlayLayer = styled(Box)({
  position: 'absolute',
  inset: 0,
  background:
    'linear-gradient(180deg, rgba(17, 28, 40, 0.04) 0%, rgba(17, 28, 40, 0.8) 50%, rgba(17, 28, 40, 0.9) 75%, rgb(17, 28, 40) 100%)',
});

const Card = styled(Box)(({ theme }) => ({
  position: 'absolute',
  left: 0,
  bottom: 0,
  width: '100%',
  maxWidth: 560,
  padding: theme.spacing(5, 4),
  borderTopRightRadius: 40,
  backgroundColor: '#F5F5F5',
  border: '3px solid #FFFFFF',
  borderBottom: 'none',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2.5),
  [theme.breakpoints.up('sm')]: {
    left: 80,
    padding: theme.spacing(6, 5),
  },
}));

export default function SignInSide(props: { disableCustomTheme?: boolean }) {
  const [searchParams] = useSearchParams();
  const { mode } = useColorScheme();
  const handleGoogleSignIn = () => {
    initiateLogin();
  };
  const reason = searchParams.get('reason');
  const reasonMessage =
    reason === 'expired' || reason === 'unauthorized'
      ? 'Hi there! You have been logged out. Your session has expired. Kindly log back in!'
      : reason === 'idle'
        ? 'Hi there! You have been logged out due to inactivity. Kindly log back in!'
        : null;

  return (
    <AppTheme {...props}>
      <CssBaseline enableColorScheme />
      <Box sx={{ position: 'relative', minHeight: '100dvh', overflow: 'hidden' }}>
        <BackgroundLayer />
        <OverlayLayer />
        <ColorModeSelect
          sx={{
            position: 'fixed',
            top: '1.5rem',
            right: '1.5rem',
            zIndex: 1,
            color: 'white',
            borderRadius: '40px',
            backgroundColor: 'rgba(255,255,255,0.1)',
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.18)' },
            '& .MuiOutlinedInput-notchedOutline': { border: '1px solid rgba(255,255,255,0.6)' },
            '&:hover .MuiOutlinedInput-notchedOutline': { border: '1px solid rgba(255,255,255,0.8)' },
            '& .MuiSelect-select': { color: 'white', py: 1, px: 2 },
            '& .MuiSvgIcon-root': { color: 'white' },
            '&:before, &:after': { display: 'none' },
          }}
        />
        <Card>
          <Box
            component="img"
            src={mode === 'dark' ? '/Empirefx_logo_white.png' : '/empire_fx_logos-01_720.png'}
            alt="Empire FX"
            sx={{ height: 32, width: 'auto', objectFit: 'contain' }}
          />
          <Box>
            <Typography
              component="h1"
              variant="h4"
              sx={{ fontWeight: 700, letterSpacing: '-0.02em', color: '#101828' }}
            >
              Welcome Back to the Empire FX Support Portal
            </Typography>
            <Typography variant="body1" sx={{ color: '#6F7285', mt: 1 }}>
              Secure internal access for Empire FX support staff and operations teams.
            </Typography>
          </Box>
          {reasonMessage && <Alert severity="info">{reasonMessage}</Alert>}
          <Button
            fullWidth
            size="large"
            onClick={handleGoogleSignIn}
            startIcon={<GoogleIcon />}
            sx={{
              height: 64,
              borderRadius: '12px',
              bgcolor: '#101828',
              color: 'white',
              fontSize: '1rem',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': { bgcolor: '#1D2939' },
            }}
          >
            Continue with Google
          </Button>
        </Card>
      </Box>
    </AppTheme>
  );
}
